import React, { useEffect, useState, useMemo } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useForm } from 'react-hook-form'

import Subtitle from '../../components/Subtitle'
import InputText from '../../components/InputText'
import Title from '../../components/Title'
import Bt from '../../components/Bt'
import KAlert from '../../components/KAlert'

// Store
import { Context } from '../../store/Store'
import { planificationApi, PLANIFICATION_MONTHS } from '../../services/planificationApi'

// Labels des mois en français
const MONTH_LABELS = {
  janvier: 'Janvier',
  fevrier: 'Février',
  mars: 'Mars',
  avril: 'Avril',
  mai: 'Mai',
  juin: 'Juin',
  juillet: 'Juillet',
  aout: 'Août',
  septembre: 'Septembre',
  octobre: 'Octobre',
  novembre: 'Novembre',
  decembre: 'Décembre',
}

// Ordre calendaire des mois (jan → déc)
const CALENDAR_MONTHS = [
  'janvier', 'fevrier', 'mars', 'avril', 'mai', 'juin',
  'juillet', 'aout', 'septembre', 'octobre', 'novembre', 'decembre'
]

const mapRecordToForm = (record) => {
  if (!record) {
    return null
  }

  const formValues = {}
  // Récupère les mois qui ont des données
  const activeMonths = []
  PLANIFICATION_MONTHS.forEach((month) => {
    const q = record.quantites?.[month]
    const value = typeof q === 'object' ? q.quantite : q
    if (value !== undefined && value !== null && value !== '') {
      formValues[month] = value
      activeMonths.push(month)
    }
  })
  formValues.surface = record.surface ?? ''
  formValues.certificat = record.certificat ?? ''
  // Read-only info from API
  formValues.atelier = record.raw?.atelier ?? ''
  formValues.unite = record.raw?.unite ?? ''
  formValues.campagne = record.raw?.campagne ?? ''
  formValues._activeMonths = activeMonths
  return formValues
}

const extractQuantitiesFromForm = (formData) =>
  PLANIFICATION_MONTHS.reduce((acc, month) => {
    const rawValue = formData[month]
    const value = Number(rawValue)
    acc[month] = Number.isNaN(value) ? 0 : Math.round(value)
    return acc
  }, {})

/**
 * Formulaire de Planification
 * @param {Object} props
 * @param {'create' | 'edit'} props.mode
 */
export function PlanificationForm({ mode = 'edit' }) {
  const navigate = useNavigate()
  const { legumeId: legumeIdParam } = useParams() // URL param for edit mode (encoded)

  const { app } = React.useContext(Context)
  const [infosPlanif, setInfosPlanif] = useState(null)
  const [loadingSubmit, setloadingSubmit] = useState(false)
  const [error, setError] = useState(null)
  const { register, handleSubmit, reset, setValue, watch } = useForm()

  // Unite mapping from API
  const [uniteMapping, setUniteMapping] = useState({})

  // All familles/sous-familles from API (full catalogue)
  const [familleSousFamilles, setFamilleSousFamilles] = useState({})

  // For create mode: Legume selection state
  const [selectedFamille, setSelectedFamille] = useState('')
  const [selectedSousFamille, setSelectedSousFamille] = useState('')

  // Mois actifs (ceux avec données + ceux ajoutés par l'utilisateur)
  const [activeMonths, setActiveMonths] = useState([])
  // Mois retiré (pour undo)
  const [removedMonth, setRemovedMonth] = useState(null) // { month: string, value: number }

  // Fetch unite mapping + full legume catalogue on mount
  useEffect(() => {
    planificationApi.getUniteMapping()
      .then(setUniteMapping)
      .catch((err) => console.error('Failed to load unite mapping:', err))
    planificationApi.getFilterOptions()
      .then((options) => setFamilleSousFamilles(options.famille_sous_familles || {}))
      .catch((err) => console.error('Failed to load filter options:', err))
  }, [])

  // Auto-dismiss du message undo après 4 secondes
  useEffect(() => {
    if (removedMonth) {
      const timer = setTimeout(() => setRemovedMonth(null), 4000)
      return () => clearTimeout(timer)
    }
  }, [removedMonth])

  // Watch certificat pour le contrôle réactif
  const watchedCertificat = watch('certificat')

  // Familles from API catalogue
  const familles = useMemo(() => {
    return Object.keys(familleSousFamilles).sort()
  }, [familleSousFamilles])

  // Sous-familles for selected famille from API catalogue
  const sousFamilles = useMemo(() => {
    if (!selectedFamille) return []
    return (familleSousFamilles[selectedFamille] || []).sort()
  }, [selectedFamille, familleSousFamilles])

  // Derive legumeCode
  const legumeCode = useMemo(() => {
    if (mode === 'edit' && legumeIdParam) {
      return decodeURIComponent(legumeIdParam)
    }
    if (mode === 'create' && selectedFamille && selectedSousFamille) {
      return `${selectedFamille}|${selectedSousFamille}`
    }
    return null
  }, [mode, legumeIdParam, selectedFamille, selectedSousFamille])

  const legumeLabel = legumeCode ? legumeCode.replace(/\|/g, ' ') : ''

  // Dynamic unite: prefer existing planif data, then mapping, then empty
  const currentUnite = infosPlanif?.unite || (legumeCode ? uniteMapping[legumeCode] : '') || ''

  // Load existing data in edit mode
  useEffect(() => {
    if (mode !== 'edit' || !legumeCode) return

    let cancelled = false
    const user = JSON.parse(window.localStorage.getItem('user') || '{}')
    const producteurCode = app.profil?.code_ean || user.nicename?.toUpperCase()
    if (!producteurCode) return

    const load = async () => {
      setloadingSubmit(true)
      try {
        const record = await planificationApi.fetch({
          producteurId: producteurCode,
          legumeId: legumeCode,
        })
        if (!cancelled && record) {
          const formData = mapRecordToForm(record)
          setInfosPlanif(formData)
          reset(formData) // Populate form
          // Set certificat explicitly for radio buttons
          if (formData.certificat) {
            setValue('certificat', formData.certificat)
          }
          // Set active months
          if (formData._activeMonths?.length > 0) {
            setActiveMonths(formData._activeMonths)
          }
        }
      } catch (err) {
        console.error(err)
      } finally {
        if (!cancelled) setloadingSubmit(false)
      }
    }
    load()
    return () => { cancelled = true }
  }, [mode, legumeCode, app.profil, reset])

  const onSubmit = async (data) => {
    if (!legumeCode) {
      setError('Veuillez sélectionner un légume.')
      return
    }

    const user = JSON.parse(window.localStorage.getItem('user') || '{}')
    const producteurCode = app.profil?.code_ean || user.nicename?.toUpperCase()
    if (!producteurCode) {
      setError('Impossible de déterminer votre code producteur.')
      return
    }

    setloadingSubmit(true)
    setError(null)
    try {
      await planificationApi.save({
        producteurId: producteurCode,
        legumeId: legumeCode,
        surface: data.surface ? Number(data.surface) : undefined,
        certificat: data.certificat,
        quantites: extractQuantitiesFromForm(data),
      })
      navigate('/planification', { replace: true })
    } catch (err) {
      console.error('Erreur sauvegarde planification:', err)
      setError('Erreur lors de la sauvegarde')
    } finally {
      setloadingSubmit(false)
    }
  }

  return (
    <div className='m-6'>
      <div className="rounded-md bg-yellow-50 border border-yellow-200 p-4 mb-4">
        <p className="text-sm text-yellow-800">
          Ce module est en cours de développement et n'est pas opérationnel. Il est uniquement présent à des fins de test.
        </p>
      </div>
      <Subtitle title={mode === 'create' ? 'Nouvelle planification' : 'Modifier la planification'} />

      {/* Mode CREATE: Sélecteurs */}
      {mode === 'create' && (
        <div className='mb-6 space-y-4'>
          <div>
            <label className='block text-sm font-medium text-gray-700 mb-1'>Famille</label>
            <select
              value={selectedFamille}
              onChange={(e) => {
                setSelectedFamille(e.target.value)
                setSelectedSousFamille('')
              }}
              className='mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-base focus:ring-base sm:text-sm'
            >
              <option value=''>-- Sélectionnez --</option>
              {familles.map((f) => (
                <option key={f} value={f}>{f}</option>
              ))}
            </select>
          </div>

          {selectedFamille && (
            <div>
              <label className='block text-sm font-medium text-gray-700 mb-1'>Sous-famille</label>
              <select
                value={selectedSousFamille}
                onChange={(e) => setSelectedSousFamille(e.target.value)}
                className='mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-base focus:ring-base sm:text-sm'
              >
                <option value=''>-- Sélectionnez --</option>
                {sousFamilles.map((sf) => (
                  <option key={sf} value={sf}>{sf.replace(/\|/g, ' ')}</option>
                ))}
              </select>
            </div>
          )}
        </div>
      )}

      {/* Mode EDIT or Legume Selected: Show Title */}
      {legumeCode && <Title title={legumeLabel} />}

      {/* Read-only info from API (mode edit only) */}
      {mode === 'edit' && infosPlanif && (infosPlanif.atelier || infosPlanif.campagne) && (
        <div className='mb-4 p-3 bg-gray-50 rounded-md border border-gray-200'>
          <div className='flex flex-wrap gap-4 text-sm text-gray-600'>
            {infosPlanif.campagne && (
              <span><strong>Campagne :</strong> {infosPlanif.campagne}</span>
            )}
            {infosPlanif.atelier && (
              <span><strong>Atelier :</strong> {infosPlanif.atelier}</span>
            )}
            {infosPlanif.unite && (
              <span><strong>Unité :</strong> {infosPlanif.unite}</span>
            )}
          </div>
        </div>
      )}

      {error && (
        <div className='bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4'>
          {error}
        </div>
      )}

      {/* Form only shown when legume is selected/known */}
      {legumeCode && (
        <form onSubmit={handleSubmit(onSubmit)}>
          <div className='border-gray-200 py-4'>
            <InputText
              type='number'
              r={{ ...register('surface') }}
              defaultValue={infosPlanif?.surface ?? ''}
              label='Surface - en Ha'
              name='surface'
              min={0}
              step='0.01'
              placeholder='Ex: 0.5'
            />
          </div>
          <div className='py-4'>
            <label className='block text-sm font-medium text-gray-700 mb-2'>Certificat</label>
            <div className='flex items-center space-x-4'>
              {['AB', 'C2', 'Semence Paysanne'].map((cert) => (
                <label key={cert} className='flex items-center cursor-pointer'>
                  <input
                    value={cert}
                    {...register('certificat')}
                    type='radio'
                    checked={watchedCertificat === cert}
                    onChange={() => setValue('certificat', cert)}
                    className='focus:ring-base h-4 w-4 text-base border-gray-300'
                  />
                  <span className='ml-2 text-sm text-gray-700'>{cert}</span>
                </label>
              ))}
            </div>
          </div>

          <div className='py-3'>
            <label className='block text-sm font-medium text-gray-700 sm:mt-px sm:pt-2'>
              Quantités mensuelles {currentUnite && <span className='text-gray-500 font-normal'>({currentUnite})</span>}
            </label>

            {/* Sélecteur pour ajouter un mois - en premier en mode création */}
            {activeMonths.length < 12 && (
              <div className='mt-3'>
                <select
                  id='add-month-select'
                  className='rounded-md border-gray-300 shadow-sm focus:border-base focus:ring-base sm:text-sm'
                  value=''
                  onChange={(e) => {
                    if (e.target.value) {
                      setActiveMonths((prev) => [...prev, e.target.value])
                    }
                  }}
                >
                  <option value=''>+ Ajouter un mois de production</option>
                  {CALENDAR_MONTHS
                    .filter((month) => !activeMonths.includes(month))
                    .map((month) => (
                      <option key={month} value={month}>{MONTH_LABELS[month]}</option>
                    ))}
                </select>
              </div>
            )}

            {/* Message d'aide si aucun mois sélectionné */}
            {activeMonths.length === 0 && (
              <p className='mt-2 text-sm text-gray-500 italic'>
                Sélectionnez les mois où vous prévoyez de produire ce légume.
              </p>
            )}

            {/* Mois actifs triés par ordre calendaire */}
            {activeMonths.length > 0 && (
              <div className='shadow rounded-md mt-4'>
                <div className='bg-white py-4 px-4 grid grid-cols-2 gap-4 sm:grid-cols-3 md:grid-cols-4'>
                  {CALENDAR_MONTHS
                    .filter((month) => activeMonths.includes(month))
                    .map((month) => (
                      <div key={month} className='relative'>
                        <InputText
                          type='number'
                          r={{ ...register(month) }}
                          defaultValue={infosPlanif?.[month] ?? ''}
                          label={MONTH_LABELS[month]}
                          name={month}
                          step='1'
                          min={0}
                          classCustom='w-full'
                          placeholder='0'
                        />
                        <button
                          type='button'
                          onClick={() => {
                            const currentValue = watch(month)
                            setRemovedMonth({ month, value: currentValue })
                            setActiveMonths((prev) => prev.filter((m) => m !== month))
                            setValue(month, '')
                          }}
                          className='absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white rounded-full text-xs hover:bg-red-600'
                          title='Retirer ce mois'
                        >
                          ×
                        </button>
                      </div>
                    ))}
                </div>
              </div>
            )}

            {/* Message undo après retrait d'un mois */}
            {removedMonth && (
              <div className='mt-3 flex items-center justify-between bg-amber-50 border border-amber-200 rounded-md px-4 py-2'>
                <span className='text-sm text-amber-800'>
                  {MONTH_LABELS[removedMonth.month]} retiré
                </span>
                <button
                  type='button'
                  onClick={() => {
                    setActiveMonths((prev) => [...prev, removedMonth.month])
                    setValue(removedMonth.month, removedMonth.value || '')
                    setRemovedMonth(null)
                  }}
                  className='text-sm font-medium text-amber-700 hover:text-amber-900 underline'
                >
                  Annuler
                </button>
              </div>
            )}

            <div className='mt-6 flex items-center space-x-4'>
              {!loadingSubmit ? (
                <>
                  <Bt title='Enregistrer' type='submit' />
                  <button
                    type='button'
                    onClick={() => navigate('/planification')}
                    className='inline-flex items-center px-6 py-3 border-2 border-gray-300 font-medium rounded-full text-gray-700 bg-white hover:bg-gray-50 hover:border-gray-400 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-300'
                  >
                    Annuler
                  </button>
                </>
              ) : (
                <div className='py-3 text-gray-500'>Patienter...</div>
              )}
            </div>
          </div>
        </form>
      )}
    </div>
  )
}
